import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { startSessionRecordingPostHog } from "@/lib/posthogAnalytics";
interface CookieBannerProps {
  onAccept?: () => void;
  onDecline?: () => void;
  onManage?: () => void;
  /**
   * `embedded` — dentro del recuadro del video (membership / US exclusive).
   * Barra full-width de una línea, touch-safe en IG WebView 320–430px.
   */
  variant?: 'fixed' | 'embedded';
  /** Retraso antes de mostrar (ms). Membership: más alto para no tapar el CTA VIP. */
  showDelayMs?: number;
}

export const CookieBanner: React.FC<CookieBannerProps> = ({
  onAccept,
  onDecline,
  onManage,
  variant = 'fixed',
  showDelayMs = 2000,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookie_consent');
    if (!cookieConsent) {
      const delay = Math.max(0, showDelayMs);
      const timer = setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [showDelayMs]);

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'functionality_storage': 'granted',
        'personalization_storage': 'granted',
        'security_storage': 'granted',
        'third_party_storage': 'granted',
      });
    }

    startSessionRecordingPostHog();

    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onAccept?.();
    }, 300);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie_consent', 'declined');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'functionality_storage': 'granted',
        'personalization_storage': 'denied',
        'security_storage': 'granted',
        'third_party_storage': 'denied',
      });
    }

    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onDecline?.();
    }, 300);
  };

  const handleManage = () => {
    onManage?.();
  };

  if (!isVisible) return null;

  const isEmbedded = variant === 'embedded';

  // ── embedded: barra full-width (mobile dual-CTA / membership / IG WebView) ──
  // Accept primero (thumb zone), copy corto, dismiss con hit area ≥36px.
  // data-cookie-compact: el CSS de Instagram no fuerza 44×44 en estos botones.
  if (isEmbedded) {
    return (
      <div
        role="region"
        aria-label={t('cookieBanner.title')}
        aria-live="polite"
        data-testid="cookie-banner-embedded"
        className={[
          'relative z-[1] w-full max-w-full pointer-events-auto',
          'transition-[opacity,transform] duration-300 ease-out',
          isAnimating
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-1 pointer-events-none',
        ].join(' ')}
      >
        <div
          className="flex w-full items-center gap-2 rounded-full border border-white/[0.10] bg-black/75 pl-1 pr-1 py-1 shadow-sm backdrop-blur-md"
          style={{
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          <button
            type="button"
            data-cookie-compact
            onClick={handleAccept}
            className="shrink-0 rounded-full bg-white px-3.5 py-2 text-[11px] font-semibold leading-none text-black active:bg-white/90 md:hover:bg-white/95 transition-colors"
            style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
          >
            {t('cookieBanner.acceptAll')}
          </button>
          <p className="min-w-0 flex-1 text-[10px] leading-snug text-white/50 line-clamp-2">
            {t('cookieBanner.descriptionShort')}
          </p>
          <button
            type="button"
            data-cookie-compact
            onClick={handleDecline}
            aria-label={t('cookieBanner.decline')}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white/50 active:text-white/85 active:bg-white/10 md:hover:text-white/70 transition-colors"
            style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // ── fixed: esquina bottom viewport (páginas no-membership) ──
  return (
    <div
      role="region"
      aria-label={t('cookieBanner.title')}
      aria-live="polite"
      data-testid="cookie-banner-fixed"
      className={[
        'transition-[opacity,transform] duration-300 ease-out',
        'fixed z-40 bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4',
        isAnimating
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-1 pointer-events-none',
      ].join(' ')}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div
        className="mx-auto sm:mx-0 max-w-md sm:max-w-sm rounded-xl border border-white/10 bg-black/75 px-3 py-2.5 shadow-lg backdrop-blur-md"
        style={{
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <div className="flex items-start gap-2.5">
          <div className="flex-1 min-w-0">
            <p className="text-[11.5px] leading-relaxed text-white/75">
              {t('cookieBanner.description')}{' '}
              <a
                href="/privacy-policy"
                className="text-white/90 underline decoration-white/30 underline-offset-2 md:hover:decoration-white/60 transition-colors"
              >
                {t('cookieBanner.privacyPolicy')}
              </a>
              {' · '}
              <a
                href="/cookie-policy"
                className="text-white/90 underline decoration-white/30 underline-offset-2 md:hover:decoration-white/60 transition-colors"
              >
                {t('cookieBanner.cookiePolicy')}
              </a>
            </p>
            <div className="flex items-center justify-center mt-2 gap-1.5">
              <button
                type="button"
                onClick={handleAccept}
                className="min-h-[40px] rounded-md bg-white/95 px-3 py-2 text-[12px] font-semibold text-black active:bg-white md:hover:bg-white transition-colors"
                style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
              >
                {t('cookieBanner.acceptAll')}
              </button>
              <button
                type="button"
                onClick={handleManage}
                className="min-h-[40px] rounded-md px-3 py-2 text-[12px] text-white/60 active:text-white/90 md:hover:text-white/90 transition-colors"
                style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
              >
                {t('cookieBanner.manage')}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDecline}
            aria-label={t('cookieBanner.decline')}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-white/40 active:text-white/70 active:bg-white/5 md:hover:text-white/70 md:hover:bg-white/5 transition-colors"
            style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
